import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth, type PackageFeatures } from "./lib/auth";
import { Shell } from "./components/layout/Shell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Packages from "./pages/Packages";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Invoices from "./pages/Invoices";
import Quotes from "./pages/Quotes";
import Contacts from "./pages/Contacts";
import Expenses from "./pages/Expenses";
import Payments from "./pages/Payments";
import Kasboek from "./pages/Kasboek";
import BankTransactions from "./pages/BankTransactions";
import Settings from "./pages/Settings";
import PayableInvoices from "./pages/PayableInvoices";
import SuperAdmin from "./pages/SuperAdmin";
import ShareView from "./pages/ShareView";

/** Voorkomt dat een gebruiker direct naar een pagina navigeert die niet in
 *  het pakket van het actieve bedrijf zit (bijv. /expenses op Budget). Stuurt
 *  dan door naar /dashboard. */
function FeatureGate({
  feature,
  children,
}: {
  feature: keyof PackageFeatures;
  children: ReactNode;
}) {
  const { features } = useAuth();
  if (!features[feature]) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PrivateRoutes() {
  const { user, loading, activeCompanyId } = useAuth();
  const location = window.location;
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Superadmin mag bedrijfs-pagina's zien zodra er een actief bedrijf is
  // (standaard het oudste/hoofdbedrijf, bijv. AICORE BV). Zonder actief
  // bedrijf (zeldzaam) sturen we naar het beheerpaneel.
  if (user.role === "SUPERADMIN") {
    if (activeCompanyId) return <Shell><Outlet /></Shell>;
    return <Navigate to="/superadmin" replace />;
  }
  if (user.companies.length === 0 && !location.pathname.startsWith("/onboarding")) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Shell><Outlet /></Shell>;
}

function AdminRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "SUPERADMIN") return <Navigate to="/dashboard" replace />;
  return <Shell><Outlet /></Shell>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/packages" element={<Packages />} />
      <Route path="/onboarding" element={<Onboarding />} />
      {/* Publieke share-link — géén auth. Klant opent document via token uit e-mail. */}
      <Route path="/share/:token" element={<ShareView />} />
      <Route element={<PrivateRoutes />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/payable-invoices" element={<PayableInvoices />} />
        <Route
          path="/quotes"
          element={
            <FeatureGate feature="quotes">
              <Quotes />
            </FeatureGate>
          }
        />
        <Route path="/contacts" element={<Contacts />} />
        <Route
          path="/expenses"
          element={
            <FeatureGate feature="expenses">
              <Expenses />
            </FeatureGate>
          }
        />
        <Route
          path="/payments"
          element={
            <FeatureGate feature="payments">
              <Payments />
            </FeatureGate>
          }
        />
        <Route
          path="/kasboek"
          element={
            <FeatureGate feature="kasboek">
              <Kasboek />
            </FeatureGate>
          }
        />
        <Route path="/settings" element={<Settings />} />
        {/* Banktransacties: in alle pakketten beschikbaar (geen FeatureGate). */}
        <Route path="/banktransacties" element={<BankTransactions />} />
      </Route>
      <Route element={<AdminRoutes />}>
        <Route path="/superadmin" element={<SuperAdmin />} />
      </Route>
    </Routes>
  );
}
